import { assign, createStateMachine, type EventObject } from '@heidi/flow/state-machine';

export type ArtifactGenerationPhase =
  | 'idle'
  | 'uploading'
  | 'awaiting'
  | 'streaming'
  | 'finalizing'
  | 'aborted'
  | 'failed';

export type ArtifactGenerationContext = {
  generationId: number;
  outgoingContent: string | null;
  firstChunkReceived: boolean;
  progress?: number;
  controller: AbortController | null;
  error: unknown;
};

type StartEvent = EventObject & {
  generationId: number;
  outgoingContent: string | null;
  controller: AbortController;
};

type ProgressEvent = EventObject & { progress?: number };
type FailureEvent = EventObject & { error: unknown };

export type ArtifactGenerationEvent =
  | (StartEvent & { type: 'START_UPLOADING' | 'START_AWAITING' })
  | { type: 'AWAIT' }
  | { type: 'STREAM_OPEN' }
  | { type: 'CHUNK' }
  | (ProgressEvent & { type: 'PROGRESS' })
  | { type: 'FINALIZE' }
  | { type: 'COMPLETE' }
  | { type: 'ABORT' }
  | (FailureEvent & { type: 'FAIL' })
  | { type: 'RESET' };

const initialContext: ArtifactGenerationContext = {
  generationId: 0,
  outgoingContent: null,
  firstChunkReceived: false,
  controller: null,
  error: null,
};

const captureStart = assign<ArtifactGenerationContext, StartEvent>((_, event) => ({
  generationId: event.generationId,
  outgoingContent: event.outgoingContent,
  firstChunkReceived: false,
  progress: undefined,
  controller: event.controller,
  error: null,
}));

const markFirstChunk = assign<ArtifactGenerationContext>(() => ({
  firstChunkReceived: true,
  outgoingContent: null,
}));

const setProgress = assign<ArtifactGenerationContext, ProgressEvent>((_, event) => ({
  progress: event.progress,
}));

const setFailure = assign<ArtifactGenerationContext, FailureEvent>((_, event) => ({
  error: event.error,
}));

const resetContext = assign<ArtifactGenerationContext>(() => initialContext);

const restartTransitions = {
  START_UPLOADING: { target: 'uploading', actions: captureStart },
  START_AWAITING: { target: 'awaiting', actions: captureStart },
};

const activeTransitions = {
  ...restartTransitions,
  STREAM_OPEN: 'streaming',
  CHUNK: { target: 'streaming', actions: markFirstChunk },
  FINALIZE: 'finalizing',
  COMPLETE: { target: 'idle', actions: resetContext },
  ABORT: 'aborted',
  FAIL: { target: 'failed', actions: setFailure },
  RESET: { target: 'idle', actions: resetContext },
};

export const artifactGenerationMachine = createStateMachine({
  id: 'artifactGeneration',
  initial: 'idle',
  context: initialContext,
  states: {
    idle: {
      on: restartTransitions,
    },
    uploading: {
      on: {
        ...activeTransitions,
        PROGRESS: { actions: setProgress },
        AWAIT: 'awaiting',
      },
    },
    awaiting: {
      on: activeTransitions,
    },
    streaming: {
      on: activeTransitions,
    },
    finalizing: {
      on: {
        ...restartTransitions,
        COMPLETE: { target: 'idle', actions: resetContext },
        ABORT: 'aborted',
        FAIL: { target: 'failed', actions: setFailure },
        RESET: { target: 'idle', actions: resetContext },
      },
    },
    aborted: {
      on: {
        ...restartTransitions,
        RESET: { target: 'idle', actions: resetContext },
      },
    },
    failed: {
      on: {
        ...restartTransitions,
        RESET: { target: 'idle', actions: resetContext },
      },
    },
  },
});
